import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, unref as _unref, createCommentVNode as _createCommentVNode, createBlock as _createBlock, withCtx as _withCtx, createVNode as _createVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "flex flex-wrap items-center justify-between gap-3 mb-4" }
const _hoisted_2 = {
  class: "inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm",
  role: "group",
  "aria-label": "Date range"
}
const _hoisted_3 = ["data-range", "onClick"]
const _hoisted_4 = {
  key: 0,
  class: "mb-4 text-sm text-red-600"
}
const _hoisted_5 = { class: "grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" }
const _hoisted_6 = { class: "text-[11px] uppercase tracking-[0.08em] text-gray-500" }
const _hoisted_7 = { class: "text-2xl font-extrabold tracking-tight text-gray-900 mt-1" }
const _hoisted_8 = { class: "grid grid-cols-1 lg:grid-cols-5 gap-3 mb-3" }
const _hoisted_9 = { class: "h-56" }
const _hoisted_10 = { class: "mt-2 space-y-2" }
const _hoisted_11 = { class: "w-4 font-extrabold text-gray-400" }
const _hoisted_12 = { class: "w-36 truncate" }
const _hoisted_13 = { class: "flex-1 h-2 bg-gray-100 rounded overflow-hidden" }
const _hoisted_14 = { class: "font-bold" }
const _hoisted_15 = {
  key: 0,
  class: "text-sm text-gray-500"
}
const _hoisted_16 = { class: "grid grid-cols-1 md:grid-cols-3 gap-3 mb-3" }
const _hoisted_17 = { class: "h-28" }
const _hoisted_18 = { class: "mt-2 space-y-1.5" }
const _hoisted_19 = { class: "w-20 text-gray-600" }
const _hoisted_20 = { class: "flex-1 h-2 bg-gray-100 rounded overflow-hidden" }
const _hoisted_21 = { class: "font-bold" }
const _hoisted_22 = { class: "flex gap-2 mt-2 text-xs" }
const _hoisted_23 = ["data-log-filter", "onClick"]
const _hoisted_24 = { class: "mt-3 divide-y divide-gray-100 text-sm" }
const _hoisted_25 = { class: "w-28 shrink-0 text-gray-400 text-xs pt-0.5" }
const _hoisted_26 = { class: "text-gray-900" }
const _hoisted_27 = {
  key: 0,
  class: "py-3 text-gray-500"
}

import { computed, defineComponent, h, inject, onMounted, ref } from 'vue'

import BarChart from '@/components/BarChart.vue'
import { useMarketing } from '@/composables/useMarketing'

import MarketingMilestones from './MarketingMilestones.vue'
import { deltaOf, formatReturn, funnelRows, money, ordersByDayChart, pct, revenueByPlayChart } from './marketingSeries'

const PAGE = 50
const NA = 'n/a'

export default {
  __name: 'MarketingPerformance',
  setup(__props) {

const Panel = defineComponent({
  props: { title: { type: String, default: '' }, caption: { type: String, default: '' } },
  setup(props, { slots }) {
    return () =>
      h('section', { class: 'bg-white rounded-2xl border border-gray-200 p-4' }, [
        h('div', { class: 'flex items-baseline justify-between gap-2' }, [
          h('h3', { class: 'text-sm font-bold text-gray-900' }, props.title),
          props.caption ? h('span', { class: 'text-xs text-gray-500' }, props.caption) : null
        ]),
        slots.default?.()
      ])
  }
})

const RANGES = [
  { label: '30d', value: '30d' },
  { label: '90d', value: '90d' },
  { label: '12m', value: '12m' },
  { label: 'All', value: 'all' }
]
const FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Plans', value: 'plan' },
  { label: 'Sends', value: 'send' },
  { label: 'Results', value: 'result' }
]

const { performance, error, loadPerformance } = inject('marketing', null) || useMarketing()
const range = ref('90d')
const filter = ref('all')
const pickFilter = value => {
  filter.value = value
  shown.value = PAGE
}
const shown = ref(PAGE)
const setRange = value => {
  range.value = value
  shown.value = PAGE
  return loadPerformance(value)
}

const TILE_LABELS = ['Revenue driven', 'Orders', 'Return on sends', 'Reached', 'Named by AI']
const countTone = d => (d === null || d === 0 ? 'text-gray-500' : d > 0 ? 'text-green-600' : 'text-red-600')
const aiCaption = d =>
  d === null ? 'no prior checks' : d === 0 ? 'level with prior period' : `${d > 0 ? '↑' : '↓'} ${Math.abs(d)}`
const tone = d => (d === null ? 'text-gray-500' : d.up ? 'text-green-600' : 'text-red-600')
const arrow = d => (d.up ? '↑' : '↓')
const tiles = computed(() => {
  if (!performance.value) return TILE_LABELS.map(label => ({ label, value: NA, caption: '', tone: 'text-gray-500' }))
  const t = performance.value.tiles || {}
  const rev = deltaOf(t.revenue_cents || 0, t.revenue_prior_cents)
  const ordersDelta = t.orders_prior === null || t.orders_prior === undefined ? null : t.orders - t.orders_prior
  const aiDiff = t.ai_total_prior ? (t.ai_named || 0) - (t.ai_named_prior || 0) : null
  return [
    {
      label: 'Revenue driven',
      value: money(t.revenue_cents),
      caption: rev ? `${arrow(rev)} ${rev.pct}% vs prior period` : 'no prior period',
      tone: tone(rev)
    },
    {
      label: 'Orders',
      value: t.orders || 0,
      caption: ordersDelta === null ? 'no prior period' : `${ordersDelta >= 0 ? '↑' : '↓'} ${Math.abs(ordersDelta)}`,
      tone: ordersDelta === null ? 'text-gray-500' : ordersDelta >= 0 ? 'text-green-600' : 'text-red-600'
    },
    {
      label: 'Return on sends',
      value: formatReturn(t.return_multiple),
      caption: 'revenue ÷ text + code cost',
      tone: 'text-gray-500'
    },
    {
      label: 'Reached',
      value: (t.reached || 0).toLocaleString('en-US'),
      caption: `texts delivered · ${t.reply_rate === null || t.reply_rate === undefined ? 'n/a' : pct(t.reply_rate)} replied`,
      tone: 'text-gray-500'
    },
    t.ai_total
      ? {
          label: 'Named by AI',
          value: `${t.ai_named} of ${t.ai_total}`,
          caption: aiCaption(aiDiff),
          tone: countTone(aiDiff)
        }
      : { label: 'Named by AI', value: 'Not checked yet', caption: 'AI checks are off', tone: 'text-gray-500' }
  ]
})

const revenueChart = computed(() => revenueByPlayChart(performance.value?.revenue_by_play || {}))
const dayChart = computed(() => ordersByDayChart(performance.value?.orders_by_day || {}))
const funnel = computed(() => funnelRows(performance.value?.funnel?.totals))
const topRevenue = computed(() => Math.max(...(performance.value?.best_plays || []).map(p => p.revenue_cents), 1))
const barWidth = p => Math.round((p.revenue_cents / topRevenue.value) * 100)
const filteredLog = computed(() =>
  (performance.value?.log || []).filter(r => filter.value === 'all' || r.kind === filter.value)
)
const visibleLog = computed(() => filteredLog.value.slice(0, shown.value))
const formatAt = at =>
  at ? new Date(at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''

const base = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }
const stacked = {
  ...base,
  plugins: { legend: { position: 'bottom' } },
  scales: {
    x: { stacked: true, grid: { display: false } },
    y: { stacked: true, beginAtZero: true, grid: { display: false } }
  }
}
const plain = {
  ...base,
  scales: { x: { grid: { display: false } }, y: { beginAtZero: true, grid: { display: false } } }
}

onMounted(() => loadPerformance(range.value))

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _cache[1] || (_cache[1] = _createElementVNode("div", null, [
        _createElementVNode("h2", { class: "text-lg font-bold text-gray-900" }, "How marketing is doing"),
        _createElementVNode("p", { class: "text-xs text-gray-500" }, "Attributed by play code · updated nightly at 05:30")
      ], -1)),
      _createElementVNode("div", _hoisted_2, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(RANGES, (r) => {
          return _createElementVNode("button", {
            key: r.value,
            type: "button",
            "data-range": r.value,
            class: _normalizeClass(["px-3 py-1.5", range.value === r.value ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50']),
            onClick: $event => (setRange(r.value))
          }, _toDisplayString(r.label), 11, _hoisted_3)
        }), 64))
      ])
    ]),
    (_unref(error))
      ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(_unref(error)), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_5, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tiles.value, (t) => {
        return (_openBlock(), _createElementBlock("div", {
          key: t.label,
          class: "bg-white rounded-2xl border border-gray-200 p-4"
        }, [
          _createElementVNode("p", _hoisted_6, _toDisplayString(t.label), 1),
          _createElementVNode("p", _hoisted_7, _toDisplayString(t.value), 1),
          _createElementVNode("p", {
            class: _normalizeClass(["text-xs font-semibold mt-1", t.tone])
          }, _toDisplayString(t.caption), 3)
        ]))
      }), 128))
    ]),
    _createElementVNode("div", _hoisted_8, [
      _createVNode(_unref(Panel), {
        class: "lg:col-span-3",
        title: "Revenue by play, month over month",
        caption: "stacked by play"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_9, [
            (_unref(performance))
              ? (_openBlock(), _createBlock(BarChart, {
                  key: 0,
                  "chart-data": revenueChart.value,
                  "chart-options": stacked
                }, null, 8, ["chart-data"]))
              : _createCommentVNode("", true)
          ])
        ]),
        _: 1
      }),
      _createVNode(_unref(Panel), {
        class: "lg:col-span-2",
        title: "Best plays",
        caption: "Revenue per play in this range"
      }, {
        default: _withCtx(() => [
          _createElementVNode("ol", _hoisted_10, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(performance)?.best_plays || [], (p, i) => {
              return (_openBlock(), _createElementBlock("li", {
                key: p.play,
                "data-best-play": "",
                class: "flex items-center gap-3 text-sm"
              }, [
                _createElementVNode("span", _hoisted_11, _toDisplayString(i + 1), 1),
                _createElementVNode("span", _hoisted_12, _toDisplayString(p.play), 1),
                _createElementVNode("span", _hoisted_13, [
                  _createElementVNode("span", {
                    class: "block h-2 bg-gray-900",
                    style: _normalizeStyle({ width: `${barWidth(p)}%` })
                  }, null, 4)
                ]),
                _createElementVNode("span", _hoisted_14, _toDisplayString(_unref(money)(p.revenue_cents)), 1)
              ]))
            }), 128)),
            (!_unref(performance)?.best_plays?.length)
              ? (_openBlock(), _createElementBlock("li", _hoisted_15, "No measured plays yet."))
              : _createCommentVNode("", true)
          ]),
          _cache[2] || (_cache[2] = _createElementVNode("p", { class: "text-xs text-gray-500 mt-3" }, "Plays with no code are not ranked.", -1))
        ]),
        _: 1
      })
    ]),
    _createElementVNode("div", _hoisted_16, [
      _createVNode(_unref(Panel), {
        title: "Orders per day",
        caption: "send days dark"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_17, [
            (_unref(performance))
              ? (_openBlock(), _createBlock(BarChart, {
                  key: 0,
                  "chart-data": dayChart.value,
                  "chart-options": plain
                }, null, 8, ["chart-data"]))
              : _createCommentVNode("", true)
          ])
        ]),
        _: 1
      }),
      _createVNode(_unref(Panel), {
        title: "From proposed to ordered",
        caption: "this range"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_18, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(funnel.value, (row) => {
              return (_openBlock(), _createElementBlock("div", {
                key: row.label,
                "data-funnel-row": "",
                class: "flex items-center gap-3 text-sm"
              }, [
                _createElementVNode("span", _hoisted_19, _toDisplayString(row.label), 1),
                _createElementVNode("span", _hoisted_20, [
                  _createElementVNode("span", {
                    class: "block h-2 bg-gray-900",
                    style: _normalizeStyle({ width: `${row.pct}%` })
                  }, null, 4)
                ]),
                _createElementVNode("span", _hoisted_21, _toDisplayString(row.value), 1)
              ]))
            }), 128))
          ])
        ]),
        _: 1
      }),
      _createVNode(_unref(Panel), {
        title: "Milestones",
        caption: "earned as you go"
      }, {
        default: _withCtx(() => [
          _createVNode(MarketingMilestones, {
            class: "mt-2",
            milestones: _unref(performance)?.milestones || []
          }, null, 8, ["milestones"])
        ]),
        _: 1
      })
    ]),
    _createVNode(_unref(Panel), { title: "What the agent did" }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_22, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(FILTERS, (f) => {
            return _createElementVNode("button", {
              key: f.value,
              type: "button",
              "data-log-filter": f.value,
              class: _normalizeClass(["rounded-full px-2.5 py-1 border", filter.value === f.value ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 text-gray-700']),
              onClick: $event => (pickFilter(f.value))
            }, _toDisplayString(f.label), 11, _hoisted_23)
          }), 64))
        ]),
        _createElementVNode("ul", _hoisted_24, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleLog.value, (row, i) => {
            return (_openBlock(), _createElementBlock("li", {
              key: i,
              "data-log-row": "",
              class: "flex gap-3 py-2"
            }, [
              _createElementVNode("span", _hoisted_25, _toDisplayString(formatAt(row.at)), 1),
              _createElementVNode("span", _hoisted_26, _toDisplayString(row.sentence), 1)
            ]))
          }), 128)),
          (!visibleLog.value.length)
            ? (_openBlock(), _createElementBlock("li", _hoisted_27, "Nothing yet."))
            : _createCommentVNode("", true)
        ]),
        (filteredLog.value.length > shown.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              type: "button",
              "data-testid": "show-more",
              class: "text-xs underline text-gray-600 mt-2",
              onClick: _cache[0] || (_cache[0] = $event => (shown.value += PAGE))
            }, " Show more "))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

}